import { Grid, IconButton, Tooltip } from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import { cloneDeep } from 'lodash-es';
import MaterialTable, { Column } from 'material-table';
import React, { ReactElement } from 'react';

import {
  IChannelGroup,
  ICountryGroup,
  IHeaderBidder,
  INameID,
  IPublisher,
  ISite,
} from '~/src/dash/api/console-api';
import { IStore } from '~/src/dash/reducers/store';
import { getRoutePath, RouteBidderSettingsExisting } from '~/src/dash/router/routes';
import { IListItem } from '~/src/dash/types/menuList';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { FLIGHT_DATES, REACTIVATE_BIDDERS } from '~/src/dash/views/campaign/common';
import {
  IAdserverCampaignWithSelector,
  IInventoryRouteInfo,
} from '~/src/dash/views/creatives/creative-library-list-view';
import { ct } from '~/src/locales/common';

// Component Note: use this component for a modal pop up table to select other items like in the bidder table
// when you click on "Targeted Channels" or "Targeted Channel Groups"

export interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  items:
    | IChannelGroup[]
    | ISite[]
    | IHeaderBidder[]
    | IAdserverCampaignWithSelector[]
    | INameID[]
    | ICountryGroup
    | Array<Partial<IPublisher>>;
  title: string;
  singularTitle: string;
  // allows the rows to be clicked on using onSelectRow
  interactive?: boolean;
  // enables selection on material table, currently used for Creative Library
  customColumns?: any[];
  enableMultiSelect?: boolean;
  // moves search bar to left side
  enableSearchBarLeft?: boolean;
  isExplicitLink?: boolean;
  // usually handles redirect to the site, headerbidder
  onSelectRow?: (id: string) => void;
  // use when material table selection is enabled to pass which rows are selected back to the parent
  handleSelectionChange?: (rows: any) => void;
  // pass in custom toolbar to show at the top of the modal
  renderCustomToolbar?: (props: any) => ReactElement;
  // rename the "name" column
  nameColumnRename?: string;
}

interface IState extends IBaseState {
  columns: Array<
    Column<IChannelGroup | ISite | IHeaderBidder | IAdserverCampaignWithSelector | INameID>
  >;
}

export const ModalSelectTable = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: () => ({}),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      columns: [
        {
          title: ct('ID'),
          field: 'ID',
          searchable: true,
          maxWidth: '100px',
        },
        {
          title: this.props.nameColumnRename ? this.props.nameColumnRename : ct('Name'),
          field: 'Name',
          render: (
            rowData:
              | IChannelGroup
              | ISite
              | IHeaderBidder
              | IAdserverCampaignWithSelector
              | IInventoryRouteInfo
              | IListItem,
          ) => {
            const { title, singularTitle, isExplicitLink, onSelectRow } = this.props;
            // @ts-ignore // we ignore here in order to allow IListItem to be useable
            const name = checkUserInfoMasked(this.props.user.user)
              ? `demo_${title}-${rowData.ID}`
              : rowData.Name || rowData.title || 'N/A';

            if (isExplicitLink && onSelectRow) {
              return (
                <div>
                  <span>{name}</span>
                  <Tooltip title={ct('TooltipOpenItem', { item: singularTitle || title })}>
                    <IconButton
                      size='small'
                      aria-label='open'
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectRow(rowData.ID.toString());
                      }}
                      style={{ padding: 14, marginLeft: 6 }}
                    >
                      <LaunchIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </div>
              );
            }

            return this.props.interactive ? (
              <Tooltip
                title={
                  'DisableTooltip' in rowData && rowData.DisableTooltip
                    ? ''
                    : ct('TooltipOpenItem', { item: singularTitle || title })
                }
              >
                <p>{name}</p>
              </Tooltip>
            ) : (
              <p>{name}</p>
            );
          },
        },
      ],
    };

    // on mount, add custom columns to the columns set, only name and ID are default
    componentDidMount = () => {
      const { customColumns } = this.props;
      const { columns } = this.state;
      if (customColumns && customColumns.length) {
        const clonedColumns = cloneDeep(columns);
        clonedColumns.push(...customColumns);
        this.setState({
          columns: clonedColumns,
        });
      }
    };

    handleRowClick = (
      _: undefined,
      rowData: IChannelGroup | ISite | IHeaderBidder | IAdserverCampaignWithSelector | INameID,
    ) => {
      if (this.props.onSelectRow) {
        this.props.onSelectRow(rowData.ID.toString());
      }
    };

    render() {
      const {
        items,
        interactive,
        enableMultiSelect,
        enableSearchBarLeft,
        title,
        handleSelectionChange,
        renderCustomToolbar,
      } = this.props;

      const { columns } = this.state;
      let fieldProps = {
        style: {
          padding: 20,
          width: '100%',
        },
        title,
        options: {
          padding: 'dense',
          sorting: true,
          pageSize: 10,
          pageSizeOptions: [5, 10, 30],
          selection: !!enableMultiSelect,
          showTextRowsSelected: false,
          showTitle: title !== '',
          maxBodyHeight: 500,
          searchFieldAlignment: enableSearchBarLeft ? 'left' : 'right',
          searchFieldStyle: {
            backgroundColor: '#F8F8F8',
            fontSize: 12,
            color: '#707070',
            paddingTop: 2,
            paddingLeft: 5,
            width: '250px',
          },
          rowStyle: (rowData: any) => {
            return {
              backgroundColor:
                rowData.tableData.id === 0 && title === FLIGHT_DATES ? '#273653' : '',
              color: rowData.tableData.id === 0 && title === FLIGHT_DATES ? 'white' : '',
            };
          },
        },
        localization: {
          toolbar: {
            searchPlaceholder: ct('Search'),
            searchTooltip: ct('Search'),
          },
          body: {
            emptyDataSourceMessage: ct('NoDataFound'),
          },
        },
        columns,
        data: items,
        onRowClick: (
          _: any,
          rowData: IChannelGroup | ISite | IHeaderBidder | IAdserverCampaignWithSelector | INameID,
        ) => {
          if (title === REACTIVATE_BIDDERS) {
            window.open(
              '#' +
                getRoutePath(RouteBidderSettingsExisting, {
                  p: this.props.session.publisher.ID,
                  b: rowData.ID,
                }),
            );
          }
        },
      };

      if (interactive && !this.props.isExplicitLink) {
        fieldProps = Object.assign(fieldProps, { onRowClick: this.handleRowClick });
      } else {
        // needed to remove cursor pointer on the row
        fieldProps = Object.assign(fieldProps, { onRowClick: undefined });
      }

      // if there is a customToolbar render function, add a custom toolbar with a button to add the mediafiles
      if (renderCustomToolbar) {
        fieldProps = Object.assign(fieldProps, {
          components: {
            Toolbar: renderCustomToolbar,
          },
        });
      }

      // separated as variable for ts-ignore
      const materialTable = (
        // @ts-ignore
        <MaterialTable
          {...fieldProps}
          onSelectionChange={(rows) => {
            if (handleSelectionChange) {
              handleSelectionChange(rows);
            }
          }}
        />
      );

      return <Grid item>{materialTable}</Grid>;
    }
  },
);
